/**
 * Header Controller
 * Manages header state, scroll behavior, and navigation interactions
 * Mobile-first responsive header with throttled performance optimizations
 */
(function($) {
  'use strict';

  const HeaderController = {
    config: {
      scrollThreshold: 10,
      throttleDelay: 100,
      transitionDuration: 300
    },

    state: {
      isScrolled: false,
      mobileMenuOpen: false
    },

    /**
     * Initialize the header controller
     */
    init() {
      this.cacheSelectors();
      this.bindEvents();
      this.checkInitialState();
    },

    /**
     * Cache DOM selectors for performance
     */
    cacheSelectors() {
      this.$header = $('.site-header');
      this.$hamburger = $('.hamburger');
      this.$mobileSidebar = $('.mobile-sidebar');
      this.$mobileBackdrop = $('.mobile-sidebar__backdrop');
      this.$body = $('body');
      this.$menuToggles = $('.mobile-sidebar__nav .menu-toggle');
      this.$accountDropdown = $('.account-dropdown');
      this.$accountDropdownToggle = $('.account-dropdown-toggle');
    },

    /**
     * Bind all event listeners
     */
    bindEvents() {
      // Throttled scroll handler for performance
      $(window).on('scroll', this.throttle(this.handleScroll.bind(this), this.config.throttleDelay));

      // Mobile menu toggle
      this.$hamburger.on('click', this.toggleMobileMenu.bind(this));

      // Close on backdrop click
      this.$mobileBackdrop.on('click', this.closeMobileMenu.bind(this));

      // Dropdown toggles (mobile) - click and keyboard
      this.$menuToggles.on('click', this.toggleDropdown.bind(this));
      this.$menuToggles.on('keydown', this.handleMenuToggleKeydown.bind(this));

      // Account dropdown toggle
      this.$accountDropdownToggle.on('click', this.toggleAccountDropdown.bind(this));
      this.$accountDropdownToggle.on('keydown', this.handleAccountDropdownKeydown.bind(this));

      // Close account dropdown on outside click
      $(document).on('click', this.handleOutsideClick.bind(this));

      // Close mobile menu on Escape key
      $(document).on('keydown', this.handleEscapeKey.bind(this));

      // Handle window resize
      $(window).on('resize', this.throttle(this.handleResize.bind(this), 250));
    },

    /**
     * Check initial scroll state on page load
     */
    checkInitialState() {
      const scrollTop = $(window).scrollTop();
      if (scrollTop >= this.config.scrollThreshold) {
        this.state.isScrolled = true;
        this.$header.addClass('site-header--scrolled');
      }
    },

    /**
     * Handle scroll events (throttled)
     */
    handleScroll() {
      const scrollTop = $(window).scrollTop();
      const shouldBeScrolled = scrollTop >= this.config.scrollThreshold;

      if (shouldBeScrolled !== this.state.isScrolled) {
        this.state.isScrolled = shouldBeScrolled;
        this.$header.toggleClass('site-header--scrolled', shouldBeScrolled);

        // Update ARIA label for screen readers
        this.$header.attr(
          'aria-label',
          shouldBeScrolled ? 'Scrolled header' : 'Top of page header'
        );
      }
    },

    /**
     * Toggle mobile menu open/close
     */
    toggleMobileMenu(e) {
      if (e) {
        e.preventDefault();
      }

      this.state.mobileMenuOpen = !this.state.mobileMenuOpen;

      this.$body.toggleClass('mobile-menu-open', this.state.mobileMenuOpen);
      this.$hamburger.attr('aria-expanded', this.state.mobileMenuOpen);

      // Focus management for accessibility
      if (this.state.mobileMenuOpen) {
        // Trap focus in sidebar when open
        const $firstLink = this.$mobileSidebar.find('a, button, [role="button"]').first();
        setTimeout(() => {
          $firstLink.focus();
        }, this.config.transitionDuration);
      } else {
        // Return focus to hamburger button when closed
        this.$hamburger.focus();
      }
    },

    /**
     * Close mobile menu
     */
    closeMobileMenu() {
      if (this.state.mobileMenuOpen) {
        this.toggleMobileMenu();
      }
    },

    /**
     * Toggle dropdown in mobile menu
     */
    toggleDropdown(e) {
      e.preventDefault();
      e.stopPropagation();

      const $toggle = $(e.currentTarget);
      const $parent = $toggle.parent();
      const $menu = $parent.find('> .child, > .grandchild').first();
      const isExpanded = $toggle.attr('aria-expanded') === 'true';

      // Close all sibling menus at the same level
      $parent.siblings().find('.menu-toggle').attr('aria-expanded', 'false');
      $parent.siblings().find('.child, .grandchild').removeClass('is-expanded');

      // Toggle this menu
      $toggle.attr('aria-expanded', !isExpanded);
      $menu.toggleClass('is-expanded', !isExpanded);
    },

    /**
     * Handle keyboard navigation for menu toggles
     */
    handleMenuToggleKeydown(e) {
      // Enter or Space to toggle
      if (e.keyCode === 13 || e.keyCode === 32) {
        e.preventDefault();
        this.toggleDropdown(e);
      }
    },

    /**
     * Handle Escape key to close mobile menu
     */
    handleEscapeKey(e) {
      if (e.keyCode === 27 && this.state.mobileMenuOpen) {
        this.closeMobileMenu();
      }
      // Also close account dropdown on Escape
      if (e.keyCode === 27 && this.$accountDropdown.hasClass('active')) {
        this.closeAccountDropdown();
      }
    },

    /**
     * Toggle account dropdown open/close
     */
    toggleAccountDropdown(e) {
      e.preventDefault();
      e.stopPropagation();

      const isActive = this.$accountDropdown.hasClass('active');

      if (isActive) {
        this.closeAccountDropdown();
      } else {
        this.openAccountDropdown();
      }
    },

    /**
     * Open account dropdown
     */
    openAccountDropdown() {
      this.$accountDropdown.addClass('active');
      this.$accountDropdownToggle.attr('aria-expanded', 'true');
    },

    /**
     * Close account dropdown
     */
    closeAccountDropdown() {
      this.$accountDropdown.removeClass('active');
      this.$accountDropdownToggle.attr('aria-expanded', 'false');
    },

    /**
     * Handle keyboard navigation for account dropdown
     */
    handleAccountDropdownKeydown(e) {
      // Enter or Space to toggle
      if (e.keyCode === 13 || e.keyCode === 32) {
        e.preventDefault();
        this.toggleAccountDropdown(e);
      }
    },

    /**
     * Handle clicks outside account dropdown to close it
     */
    handleOutsideClick(e) {
      const $target = $(e.target);

      // Check if click is outside the account dropdown
      if (this.$accountDropdown.hasClass('active') &&
          !$target.closest('.account-dropdown').length) {
        this.closeAccountDropdown();
      }
    },

    /**
     * Handle window resize
     * Close mobile menu if resizing to desktop
     */
    handleResize() {
      const windowWidth = $(window).width();

      // Close mobile menu if window is resized to desktop breakpoint
      if (windowWidth >= 1080 && this.state.mobileMenuOpen) {
        this.closeMobileMenu();
      }
    },

    /**
     * Throttle utility function
     * Limits function execution to once per specified wait time
     * @param {Function} func - Function to throttle
     * @param {Number} wait - Wait time in milliseconds
     * @return {Function} Throttled function
     */
    throttle(func, wait) {
      let timeout = null;
      let previous = 0;

      return function(...args) {
        const now = Date.now();
        const remaining = wait - (now - previous);

        if (remaining <= 0 || remaining > wait) {
          if (timeout) {
            clearTimeout(timeout);
            timeout = null;
          }
          previous = now;
          func.apply(this, args);
        } else if (!timeout) {
          timeout = setTimeout(() => {
            previous = Date.now();
            timeout = null;
            func.apply(this, args);
          }, remaining);
        }
      };
    }
  };

  // Initialize on DOM ready
  $(document).ready(function() {
    HeaderController.init();
  });

  // Expose for potential external use
  window.HeaderController = HeaderController;

})(jQuery);
